// ── Keyword lookup helpers ───────────────────────────────────────
// 集中两类查询：
//   1) 当前 state 里的 market-data keywords（包含别名兼容）
//   2) keyword → searches30d 的查找映射（联合 keywordLibrary + market data）
//
// 历史背景：step6-revision.js 与 step3/generation-result.js 各自重写了这套逻辑，
// 字段别名 (`关键词` / `keyword`, `30天搜索量` / `searches30d`) 容易在一处升级时
// 另一处没跟。本模块作为唯一来源。

import { getState } from './state.js';

/**
 * 读取当前 state 中的 market-data 关键词；兼容旧字段名 mcpKeywords。
 * @returns {Array<{[k:string]: any}>}
 */
export function getMarketDataKeywords() {
  return getState('marketDataKeywords') || getState('mcpKeywords') || [];
}

/**
 * 从单条 market-data entry 抽取关键词字符串。
 * 兼容 sellersprite 的中文字段名（`关键词`）和英文字段名（`keyword`）。
 * @param {object} entry
 * @returns {string}
 */
export function extractKeywordText(entry) {
  return entry?.关键词 || entry?.keyword || '';
}

/**
 * 从单条 market-data entry 抽取月搜索量。
 * 兼容 `30天搜索量` 和 `searches30d`。
 * @param {object} entry
 * @returns {number}
 */
export function extractSearchVolume(entry) {
  return Number(entry?.['30天搜索量'] || entry?.searches30d || 0) || 0;
}

/**
 * 构建 `keyword (lowercase) → searches30d` 查找表。
 * keywordLibrary 优先（更精准），market-data 仅填充库里没有的。
 * @returns {Map<string, number>}
 */
export function buildVolumeMap() {
  const map = new Map();
  const lib = getState('keywordLibrary');
  if (lib?.keywords) {
    for (const k of lib.keywords) {
      const text = String(k.keyword || '').toLowerCase();
      if (text) map.set(text, k.searches30d || 0);
    }
  }
  for (const k of getMarketDataKeywords()) {
    const text = String(extractKeywordText(k) || '').toLowerCase();
    if (text && !map.has(text)) {
      map.set(text, extractSearchVolume(k));
    }
  }
  return map;
}

/**
 * 给一组裸关键词字符串补全 searches30d。
 *
 * @param {Iterable<string>} rawKeywords
 * @param {Map<string,number>} [volumeMap] - 可选预算好的 map，避免在循环里反复构建
 * @returns {Array<{keyword:string, searches30d:number}>}
 */
export function enrichKeywordsWithVolume(rawKeywords, volumeMap) {
  const map = volumeMap || buildVolumeMap();
  const out = [];
  for (const raw of rawKeywords || []) {
    const kw = String(raw || '').trim();
    if (!kw) continue;
    out.push({ keyword: kw, searches30d: map.get(kw.toLowerCase()) || 0 });
  }
  return out;
}
